import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  PropsWithChildren,
} from 'react';
import { useAppAuth } from '../../context/use-app-auth';
import {
  getLocationSettings,
  updateLocationSettings,
  LocationSettings,
} from 'src/api/services/settings-service';

interface SettingsContextType {
  guidelines: string;
  // eslint-disable-next-line no-unused-vars
  setGuidelines: (value: string) => void;
  autoReplyEnabled: boolean;
  // eslint-disable-next-line no-unused-vars
  setAutoReplyEnabled: (value: boolean) => void;
  saving: boolean;
  loaded: boolean;
  // Persists the current settings; optional overrides allow saving a single field (e.g., logo) right away.
  saveGuidelines: (overrides?: Partial<LocationSettings>) => Promise<void>;
  reviewLandingMessage: string;
  // eslint-disable-next-line no-unused-vars
  setReviewLandingMessage: (value: string) => void;
  checkinsWelcomeSubject: string;
  // eslint-disable-next-line no-unused-vars
  setCheckinsWelcomeSubject: (value: string) => void;
  checkinsWelcomeMessage: string;
  // eslint-disable-next-line no-unused-vars
  setCheckinsWelcomeMessage: (value: string) => void;
  logoUrl?: string;
  // eslint-disable-next-line no-unused-vars
  setLogoUrl: (value: string | undefined) => void;
  checkinsOfferPercent?: number;
  // eslint-disable-next-line no-unused-vars
  setCheckinsOfferPercent: (value: number | undefined) => void;
  checkinsOfferValidity?: number;
  // eslint-disable-next-line no-unused-vars
  setCheckinsOfferValidity: (value: number | undefined) => void;
  emailTemplateHtml: string;
  // eslint-disable-next-line no-unused-vars
  setEmailTemplateHtml: (value: string) => void;
  checkinsOfferCode?: string;
  // eslint-disable-next-line no-unused-vars
  setCheckinsOfferCode: (value: string | undefined) => void;
  checkinsOfferDetails?: string;
  // eslint-disable-next-line no-unused-vars
  setCheckinsOfferDetails: (value: string | undefined) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined,
);

export const useSettings = (): SettingsContextType => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
};

const defaultSettings = {
  reviewReplyGuidelines: '',
  autoReplyEnabled: false,
  reviewLandingMessage: 'Review and get 20% discount',
  checkinsWelcomeSubject: 'Thanks for checking in!',
  checkinsWelcomeMessage:
    'Hi there! Thanks for checking in with us. Here is an offer for your next visit.',
  logoUrl: undefined as string | undefined,
  emailTemplateHtml: '',
  checkinsOfferPercent: undefined as number | undefined,
  checkinsOfferValidity: undefined as number | undefined,
  checkinsOfferCode: undefined as string | undefined,
  checkinsOfferDetails: undefined as string | undefined,
};

export const SettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { location } = useAppAuth();
  const [guidelines, setGuidelines] = useState(
    defaultSettings.reviewReplyGuidelines,
  );
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(
    defaultSettings.autoReplyEnabled,
  );
  const [reviewLandingMessage, setReviewLandingMessage] = useState(
    defaultSettings.reviewLandingMessage,
  );
  const [checkinsWelcomeSubject, setCheckinsWelcomeSubject] = useState(
    defaultSettings.checkinsWelcomeSubject,
  );
  const [checkinsWelcomeMessage, setCheckinsWelcomeMessage] = useState(
    defaultSettings.checkinsWelcomeMessage,
  );
  const [logoUrl, setLogoUrl] = useState<string | undefined>(
    defaultSettings.logoUrl,
  );
  const [checkinsOfferPercent, setCheckinsOfferPercent] = useState<
    number | undefined
  >(defaultSettings.checkinsOfferPercent);
  const [checkinsOfferValidity, setCheckinsOfferValidity] = useState<
    number | undefined
  >(defaultSettings.checkinsOfferValidity);
  const [emailTemplateHtml, setEmailTemplateHtml] = useState<string>(
    defaultSettings.emailTemplateHtml,
  );
  const [checkinsOfferCode, setCheckinsOfferCode] = useState<
    string | undefined
  >(defaultSettings.checkinsOfferCode);
  const [checkinsOfferDetails, setCheckinsOfferDetails] = useState<
    string | undefined
  >(defaultSettings.checkinsOfferDetails);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const applySettings = useCallback((settings?: LocationSettings) => {
    setGuidelines(
      settings?.reviewReplyGuidelines ?? defaultSettings.reviewReplyGuidelines,
    );
    setAutoReplyEnabled(
      settings?.autoReplyEnabled ?? defaultSettings.autoReplyEnabled,
    );
    setReviewLandingMessage(
      settings?.reviewLandingMessage ?? defaultSettings.reviewLandingMessage,
    );
    setCheckinsWelcomeSubject(
      settings?.checkinsWelcomeSubject ??
        defaultSettings.checkinsWelcomeSubject,
    );
    setCheckinsWelcomeMessage(
      settings?.checkinsWelcomeMessage ??
        defaultSettings.checkinsWelcomeMessage,
    );
    setLogoUrl(settings?.logoUrl ?? defaultSettings.logoUrl);
    setEmailTemplateHtml(
      settings?.emailTemplateHtml ?? defaultSettings.emailTemplateHtml,
    );
    setCheckinsOfferPercent(
      typeof settings?.checkinsOfferPercent === 'number' &&
        !Number.isNaN(settings.checkinsOfferPercent)
        ? settings.checkinsOfferPercent
        : defaultSettings.checkinsOfferPercent,
    );
    setCheckinsOfferValidity(
      typeof settings?.checkinsOfferValidity === 'number' &&
        !Number.isNaN(settings.checkinsOfferValidity)
        ? settings.checkinsOfferValidity
        : defaultSettings.checkinsOfferValidity,
    );
    setCheckinsOfferCode(
      settings?.checkinsOfferCode ?? defaultSettings.checkinsOfferCode,
    );
    setCheckinsOfferDetails(
      settings?.checkinsOfferDetails ?? defaultSettings.checkinsOfferDetails,
    );
  }, []);

  // When location changes, keep existing values until we load fresh data.
  // If no location is selected, reset to defaults.
  useEffect(() => {
    if (!location?.key) {
      applySettings(undefined);
      setLoaded(false);
    }
  }, [location?.key, applySettings]);

  // Load existing settings when a location is available
  useEffect(() => {
    if (!location?.key) return;
    const load = async () => {
      setLoaded(false);
      const result = await getLocationSettings(location.key);
      if (result.success && result.data?.settings) {
        applySettings(result.data.settings);
      } else {
        // Preserve any existing state if we failed to load settings.
        console.warn('No settings found for location; keeping current values');
      }
      setLoaded(true);
    };
    load();
  }, [location?.key, applySettings]);

  const saveGuidelines = useCallback(
    async (overrides?: Partial<LocationSettings>) => {
      if (!location?.key) return;
      setSaving(true);
      try {
        const safeSettings: LocationSettings = {
          reviewReplyGuidelines:
            guidelines ?? defaultSettings.reviewReplyGuidelines,
          autoReplyEnabled:
            autoReplyEnabled ?? defaultSettings.autoReplyEnabled,
          reviewLandingMessage:
            reviewLandingMessage ?? defaultSettings.reviewLandingMessage,
          checkinsWelcomeSubject:
            checkinsWelcomeSubject ?? defaultSettings.checkinsWelcomeSubject,
          checkinsWelcomeMessage:
            checkinsWelcomeMessage ?? defaultSettings.checkinsWelcomeMessage,
          logoUrl: logoUrl ?? defaultSettings.logoUrl ?? '',
          emailTemplateHtml:
            emailTemplateHtml ?? defaultSettings.emailTemplateHtml ?? '',
          checkinsOfferPercent:
            checkinsOfferPercent ?? defaultSettings.checkinsOfferPercent ?? 0,
          checkinsOfferValidity:
            checkinsOfferValidity ?? defaultSettings.checkinsOfferValidity ?? 0,
          checkinsOfferCode:
            checkinsOfferCode ?? defaultSettings.checkinsOfferCode ?? '',
          checkinsOfferDetails:
            checkinsOfferDetails ?? defaultSettings.checkinsOfferDetails ?? '',
        };

        const settings: LocationSettings = {
          ...safeSettings,
          // Allow callers (like logo upload) to push a single field immediately.
          ...(overrides || {}),
        };
        const result = await updateLocationSettings(location.key, settings);
        console.log(result);
      } catch (e) {
        console.error('Failed to save settings', e);
      } finally {
        setSaving(false);
      }
    },
    [
      location?.key,
      guidelines,
      autoReplyEnabled,
      reviewLandingMessage,
      checkinsWelcomeSubject,
      checkinsWelcomeMessage,
      logoUrl,
      checkinsOfferPercent,
      checkinsOfferValidity,
      checkinsOfferCode,
      checkinsOfferDetails,
      emailTemplateHtml,
    ],
  );

  const value: SettingsContextType = {
    guidelines,
    setGuidelines,
    autoReplyEnabled,
    setAutoReplyEnabled,
    saving,
    loaded,
    saveGuidelines,
    reviewLandingMessage,
    setReviewLandingMessage,
    checkinsWelcomeSubject,
    setCheckinsWelcomeSubject,
    checkinsWelcomeMessage,
    setCheckinsWelcomeMessage,
    logoUrl,
    setLogoUrl,
    emailTemplateHtml,
    setEmailTemplateHtml,
    checkinsOfferPercent,
    setCheckinsOfferPercent,
    checkinsOfferValidity,
    setCheckinsOfferValidity,
    checkinsOfferCode,
    setCheckinsOfferCode,
    checkinsOfferDetails,
    setCheckinsOfferDetails,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
